// GymViz brand mark + wordmark.
//
// The mark is the canonical PNG provided by the brand owner. Sourced
// from document/ui_design_change/logo.png and vendored into
// ui/src/assets/. We import the file via Vite's asset-URL transform so
// the production build emits it under /assets/<hash>-logo.<hash>.png —
// that path is served directly by the cloud nginx vhost (location
// /assets/), which the served public-root path /logo.png is NOT. See
// scripts/provision-tenant.sh nginx block for the routing rules.
//
// Earlier iterations attempted a hand-crafted SVG approximation; that's
// been retired in favor of the source asset for pixel-perfect fidelity.
// The wordmark is still rendered as text so it inherits the locale font
// (Inter on en, Tajawal on ar) without us shipping multiple font glyphs.

import appIconUrl from "@/assets/apple-touch-icon.png";
import logoUrl from "@/assets/logo.png";
import { cn } from "@/lib/utils/cn";

type Variant = "mark" | "wordmark" | "full" | "app-icon";

interface GymVizLogoProps {
  /** Which composition to render. ``full`` is the default for headers. */
  variant?: Variant;
  /** Size in pixels for the mark portion. The wordmark scales proportionally
   * in ``full`` mode. Default 40 (matches the existing 40px sidebar slot). */
  size?: number;
  /** Show the "MANAGEMENT SOFTWARE" tagline under the wordmark.
   * Only meaningful for ``wordmark`` and ``full`` variants. Default true. */
  withTagline?: boolean;
  className?: string;
  /** Override the accessible label. Defaults to "GymViz". */
  ariaLabel?: string;
}

export function GymVizLogo({
  variant = "full",
  size = 40,
  withTagline = true,
  className,
  ariaLabel = "GymViz",
}: GymVizLogoProps) {
  if (variant === "mark") {
    return <Mark size={size} className={className} ariaLabel={ariaLabel} />;
  }
  if (variant === "app-icon") {
    return <AppIcon size={size} className={className} ariaLabel={ariaLabel} />;
  }
  if (variant === "wordmark") {
    return (
      <Wordmark
        height={size}
        withTagline={withTagline}
        className={className}
        ariaLabel={ariaLabel}
      />
    );
  }
  // ``full``: mark + wordmark side-by-side.
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      className={cn("inline-flex items-center gap-2", className)}
    >
      <Mark size={size} ariaLabel="" />
      <Wordmark height={size} withTagline={withTagline} ariaLabel="" />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Mark: the canonical PNG, served from /logo.png
// ---------------------------------------------------------------------------

interface MarkProps {
  size: number;
  className?: string;
  ariaLabel: string;
}

function Mark({ size, className, ariaLabel }: MarkProps) {
  // Empty alt + aria-hidden when nested inside the ``full`` wrapper so screen
  // readers don't announce "GymViz" twice. Otherwise expose the label.
  const a11y = ariaLabel
    ? { alt: ariaLabel }
    : { alt: "", "aria-hidden": true as const };
  return (
    <img
      src={logoUrl}
      width={size}
      height={size}
      // Source PNG is 279×290 (slightly taller than wide). object-contain
      // preserves the source aspect ratio inside the size box; shrink-0
      // prevents flexbox parents from squashing it.
      className={cn("shrink-0 object-contain", className)}
      style={{ width: size, height: size }}
      {...a11y}
    />
  );
}

// ---------------------------------------------------------------------------
// Wordmark: "GYMVIZ" + optional tagline
// ---------------------------------------------------------------------------

interface WordmarkProps {
  /** Height of the wordmark in pixels — matches the mark's size in ``full`` mode. */
  height: number;
  withTagline: boolean;
  className?: string;
  ariaLabel: string;
}

function Wordmark({ height, withTagline, className, ariaLabel }: WordmarkProps) {
  // The wordmark is rendered as text rather than an image so it inherits
  // the tenant's locale fonts (Inter on en, Tajawal on ar). Color reads from
  // the current ink-900 token so it inverts cleanly in dark mode.
  const a11y = ariaLabel
    ? { role: "img" as const, "aria-label": ariaLabel }
    : { "aria-hidden": true };
  const taglineFontSize = Math.max(8, Math.round(height * 0.22));
  const wordmarkFontSize = Math.max(14, Math.round(height * 0.5));

  return (
    <span
      {...a11y}
      className={cn("flex flex-col leading-none text-ink-900", className)}
      style={{ height }}
    >
      <span
        className="font-bold tracking-tight"
        style={{ fontSize: wordmarkFontSize, lineHeight: 1 }}
      >
        GYMVIZ
      </span>
      {withTagline && (
        <span
          className="font-medium uppercase text-ink-500 tracking-[0.18em] mt-0.5"
          style={{ fontSize: taglineFontSize, lineHeight: 1 }}
        >
          Management Software
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// AppIcon: served apple-touch-icon.png. Used by the manifest + favicon-large.
// ---------------------------------------------------------------------------

interface AppIconProps {
  size: number;
  className?: string;
  ariaLabel: string;
}

function AppIcon({ size, className, ariaLabel }: AppIconProps) {
  // The pre-baked apple-touch-icon.png has the white outer frame already
  // composited in (see ImageMagick command in the commit message). iOS auto-
  // rounds the corners on home-screen install; the rounded-[22%] mirrors that
  // when this asset is rendered in-app (e.g. an "About" page).
  return (
    <img
      src={appIconUrl}
      alt={ariaLabel || ""}
      aria-hidden={ariaLabel ? undefined : true}
      width={size}
      height={size}
      className={cn("shrink-0 rounded-[22%]", className)}
      style={{ width: size, height: size }}
    />
  );
}
